import { CloseIcon, UseFilterIcon } from '@/components/Icons';
import ToggleSwitch from '@/components/ToggleSwitch';
import { GradientButton } from '@/pages/Home/Generator/components/AudioEditor/EditorButton';
import StyleFilterStrength from '@/pages/Home/Generator/components/NewStyleFilter/StyleFilterStrength';
import { useGeneratorFilterStore } from '@/state/generator-filter-store';
import { cn, titleCaseToKebabCase } from '@/utils';
import { filter, isEmpty } from 'lodash-es';
import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { FilterGroup, SfListItem } from '@/service/type/Filter';
import { InfoIcon } from '@/pages/Home/Generator/components/NewStyleFilter/StyleFilterList';
import { useRenewFilterModal } from '@/pages/Home/Generator/components/SimpleEffects/AddFilterIcon/useRenewFilterModal';
import { useNavigate } from 'react-router-dom';
import { useController } from 'react-hook-form';
import useGeneratorStore from '@/state/generator-store';
import { getInitials } from '@/utils';
import { getFilterBalance } from '@/pages/Home/Generator/utils/getFilterBalance';

interface StyleFilterDetailCard {
  background?: string;
  sfId?: number;
  onRemove?: () => void;
  onUse?: ({ value, id }: { value: number; id: number | undefined }) => void;
  onSwitch?: ({ value, id }: { value: number; id: number | undefined }) => void;
  title?: string;
  name?: string;
  content?: string;
  totalCount?: number;
  leftCount?: number;
  showButtons?: boolean;
  disabled?: boolean;
  isMarket?: boolean;
  isGenerator?: boolean;
  coverStyle?: CSSProperties;
  className?: string;
  innerClassName?: string;
  fontSize?: number;
  switchDisabled?: boolean;
  defaultValue?: 'LOW' | 'HIGH' | 'NONE';
  rightTagClassname?: string;
  imageClassName?: string;
  sfList?: SfListItem[];
  selectedSfs?: FilterGroup[];
}

const valueMapWord: Record<0 | 1, string> = {
  0: 'LOW',
  1: 'HIGH',
};

const wordToValueMap: Record<string, 0 | 1> = {
  LOW: 0,
  HIGH: 1,
  NONE: 0,
};

const StyleFilterDetailCard: React.FC<StyleFilterDetailCard> = ({
  sfId,
  background,
  onRemove,
  onUse,
  onSwitch,
  title,
  content,
  name,
  totalCount = 0,
  showButtons = true,
  isMarket = false,
  coverStyle,
  className,
  innerClassName,
  fontSize = 12,
  switchDisabled = false,
  defaultValue = 'LOW',
  rightTagClassname,
  imageClassName,
  sfList,
  selectedSfs,
}) => {
  const [value, setValue] = useState(wordToValueMap[defaultValue] || 0);

  const { currentFilterGroup, currentStrength } = useGeneratorFilterStore();
  const { control } = useGeneratorStore();

  const showAdvancedStrengthControl = selectedSfs?.length === 1 ? /^(al_|es_)/.test(selectedSfs[0].code?.toLowerCase() || '') : false;

  useEffect(() => {
    setValue(wordToValueMap[defaultValue] || 0);
  }, [defaultValue]);

  const useButtonDisabled = useMemo(() => {
    if (!sfId) {
      return true;
    }
    if (totalCount === 0) {
      return true;
    }
    return false;
  }, [sfId, totalCount, currentStrength]);

  const removeButtonDisabled = useMemo(() => {
    if (!sfId) {
      return true;
    }
    if (currentFilterGroup?.id !== sfId) {
      return true;
    }
    return false;
  }, [sfId, currentFilterGroup, currentStrength]);

  const timeCount = totalCount >= 0 ? `${totalCount}` : 'unlimited';

  const showRenewButton = useMemo(() => {
    if (isMarket) {
      return false;
    }
    if (totalCount <= 3 && totalCount >= 0) {
      return true;
    }
    return false;
  }, [totalCount]);

  const navigate = useNavigate();

  const {
    field: { onChange: setStyleFilterId, value: styleFilterId },
  } = useController({
    name: 'styleFilterId',
    control: control || undefined,
  });

  const {
    field: { onChange: onChangeStyleFilterIsOpened, value: styleFilterIsOpened },
  } = useController({
    name: 'styleFilterIsOpened',
    control: control || undefined,
  });

  const {
    field: { onChange: setStyleFilterWeight, value: filterWeight },
  } = useController({
    name: 'filterWeight',
    control: control || undefined,
  });

  const { open, Modal: RenewFilterModal } = useRenewFilterModal({
    onRenew: () => {
      if (name) {
        navigate(`/marketplace/filter/${titleCaseToKebabCase(name)}`);
      }
    },
  });

  return (
    <div
      className={cn('flex-1 min-w-0 rounded-lg', className)}
      style={{
        border: isMarket ? 'none' : '1px solid var(--_Lil-Bottom-Highlight, rgba(255, 255, 255, 0.00))',
        background: isMarket ? '' : 'linear-gradient(180deg, #2B2E30,#383A3C)',
        backgroundOrigin: 'border-box',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div
        className="h-full w-full rounded-lg"
        style={{
          background: isMarket ? '' : 'var(--_Inside-Dark, linear-gradient(0deg, #191A1B 0%, #1E2021 100%))',
        }}
      >
        <div className="h-full flex flex-col justify-between items-center">
          {((sfId && selectedSfs && selectedSfs.length === 1) || (sfId && !selectedSfs)) && (
            <div
              className={cn('relative w-full h-full rounded-2xl', imageClassName)}
              style={{
                backgroundImage: `url(${background})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                minHeight: '273px',
                outline: '1px solid rgba(255, 255, 255, 0.15)',
                outlineOffset: '-1px',
                ...coverStyle,
              }}
            >
              {/* <TurnOningIcon className=" absolute top-[6px] left-[6px]" /> */}

              <div className={cn('absolute top-2 right-2', rightTagClassname)}>
                <svg width="5" height="39" viewBox="0 0 5 39" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M4.35047 7.60132C4.35047 7.66732 4.29647 7.72132 4.23047 7.72132H0.270491C0.204491 7.72132 0.150491 7.66732 0.150491 7.60132L0.150491 6.43132C0.150491 5.71133 0.666489 5.17733 1.41049 5.17733C1.92648 5.17733 2.29848 5.47733 2.52648 5.84933C2.56848 5.94533 2.62248 5.93933 2.70648 5.89733L4.15247 5.20133C4.26047 5.14733 4.35047 5.20133 4.35047 5.30333V5.63933C4.35047 5.72933 4.30847 5.77133 4.23647 5.80733L2.73048 6.52732C2.68848 6.54532 2.66448 6.56932 2.66448 6.62932V7.08532C2.66448 7.12132 2.68848 7.14532 2.72448 7.14532H4.23047C4.29647 7.14532 4.35047 7.19932 4.35047 7.26532V7.60132ZM2.04048 7.14532C2.07648 7.14532 2.10048 7.12132 2.10048 7.08532V6.43732C2.10048 6.06533 1.78848 5.77133 1.41049 5.77133C0.990487 5.77133 0.720489 6.06533 0.720489 6.43732L0.720489 7.08532C0.720489 7.12132 0.744489 7.14532 0.780488 7.14532H2.04048Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.35047 10.5281C4.35047 10.5941 4.29647 10.6481 4.23047 10.6481H0.270491C0.204491 10.6481 0.150491 10.5941 0.150491 10.5281L0.150491 8.44015C0.150491 8.37415 0.204491 8.32015 0.270491 8.32015H0.600489C0.666489 8.32015 0.720489 8.37415 0.720489 8.44015L0.720489 10.0121C0.720489 10.0481 0.744489 10.0721 0.780488 10.0721H1.84248C1.87848 10.0721 1.90248 10.0481 1.90248 10.0121V8.50015C1.90248 8.43415 1.95648 8.38015 2.02248 8.38015H2.35248C2.41848 8.38015 2.47248 8.43415 2.47248 8.50015V10.0121C2.47248 10.0481 2.49648 10.0721 2.53248 10.0721H3.72047C3.75647 10.0721 3.78047 10.0481 3.78047 10.0121V8.42215C3.78047 8.35615 3.83447 8.30215 3.90047 8.30215H4.23047C4.29647 8.30215 4.35047 8.35615 4.35047 8.42215V10.5281Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.35047 12.813C4.35047 12.879 4.29647 12.933 4.23047 12.933H0.786488C0.750488 12.933 0.726489 12.957 0.726489 12.993V13.9109C0.726489 13.9769 0.672489 14.0309 0.606489 14.0309H0.270491C0.204491 14.0309 0.150491 13.9769 0.150491 13.9109L0.150491 11.379C0.150491 11.313 0.204491 11.259 0.270491 11.259H0.606489C0.672489 11.259 0.726488 11.313 0.726488 11.379L0.726489 12.297C0.726489 12.333 0.750488 12.357 0.786488 12.357H4.23047C4.29647 12.357 4.35047 12.411 4.35047 12.477V12.813Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.35047 16.5378C4.35047 16.6038 4.29647 16.6578 4.23047 16.6578H0.270491C0.204491 16.6578 0.150491 16.6038 0.150491 16.5378L0.150491 16.2018C0.150491 16.1358 0.204491 16.0818 0.270491 16.0818H3.71447C3.75047 16.0818 3.77447 16.0578 3.77447 16.0218V14.4198C3.77447 14.3538 3.82847 14.2998 3.89447 14.2998H4.23047C4.29647 14.2998 4.35047 14.3538 4.35047 14.4198V16.5378Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.35047 19.8666C4.35047 19.9326 4.29647 19.9866 4.23047 19.9866H3.89447C3.82847 19.9866 3.77447 19.9326 3.77447 19.8666V19.0506H0.726489V19.8066C0.726489 19.8726 0.672489 19.9266 0.606489 19.9266H0.270491C0.204491 19.9266 0.150491 19.8726 0.150491 19.8066L0.150491 17.7186C0.150491 17.6526 0.204491 17.5986 0.270491 17.5986H0.606489C0.672489 17.5986 0.726488 17.6526 0.726488 17.7186L0.726489 18.4746H3.77447V17.6586C3.77447 17.5926 3.82847 17.5386 3.89447 17.5386H4.23047C4.29647 17.5386 4.35047 17.5926 4.35047 17.6586V19.8666Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.35047 22.7034C4.35047 22.7694 4.29647 22.8234 4.23047 22.8234H0.270491C0.204491 22.8234 0.150491 22.7694 0.150491 22.7034L0.150491 20.6454C0.150491 20.5794 0.204491 20.5254 0.270491 20.5254H0.600489C0.666489 20.5254 0.720489 20.5794 0.720489 20.6454L0.720489 22.1874C0.720489 22.2234 0.744489 22.2474 0.780488 22.2474H1.87848C1.91448 22.2474 1.93848 22.2234 1.93848 22.1874V20.7054C1.93848 20.6394 1.99248 20.5854 2.05848 20.5854H2.38848C2.45448 20.5854 2.50848 20.6394 2.50848 20.7054V22.1874C2.50848 22.2234 2.53248 22.2474 2.56848 22.2474H4.23047C4.29647 22.2474 4.35047 22.3014 4.35047 22.3674V22.7034Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.35047 25.8222C4.35047 25.8882 4.29647 25.9422 4.23047 25.9422H0.270491C0.204491 25.9422 0.150491 25.8882 0.150491 25.8222L0.150491 23.7342C0.150491 23.6682 0.204491 23.6142 0.270491 23.6142H0.600489C0.666489 23.6142 0.720489 23.6682 0.720489 23.7342L0.720489 25.3062C0.720489 25.3422 0.744489 25.3662 0.780488 25.3662H1.84248C1.87848 25.3662 1.90248 25.3422 1.90248 25.3062V23.7942C1.90248 23.7282 1.95648 23.6742 2.02248 23.6742H2.35248C2.41848 23.6742 2.47248 23.7282 2.47248 23.7942V25.3062C2.47248 25.3422 2.49648 25.3662 2.53248 25.3662H3.72047C3.75647 25.3662 3.78047 25.3422 3.78047 25.3062V23.7162C3.78047 23.6502 3.83447 23.5962 3.90047 23.5962H4.23047C4.29647 23.5962 4.35047 23.6502 4.35047 23.7162V25.8222Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.35047 28.773C4.35047 28.839 4.29647 28.893 4.23047 28.893H0.270491C0.204491 28.893 0.150491 28.839 0.150491 28.773L0.150491 28.437C0.150491 28.371 0.204491 28.317 0.270491 28.317H3.71447C3.75047 28.317 3.77447 28.293 3.77447 28.257V26.655C3.77447 26.589 3.82847 26.535 3.89447 26.535H4.23047C4.29647 26.535 4.35047 26.589 4.35047 26.655V28.773Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.35047 31.1658C4.35047 31.2318 4.29647 31.2858 4.23047 31.2858H2.88048L0.324491 32.4258C0.234491 32.4618 0.150491 32.3958 0.150491 32.3178L0.150491 31.9578C0.150491 31.9038 0.174491 31.8738 0.240491 31.8438L2.09448 31.0398C2.13048 31.0218 2.13048 30.9678 2.09448 30.9498L0.240491 30.1518C0.174491 30.1218 0.150491 30.0918 0.150491 30.0378L0.150491 29.6778C0.150491 29.5998 0.234491 29.5338 0.32449 29.5698L2.88048 30.7098H4.23047C4.29647 30.7098 4.35047 30.7638 4.35047 30.8298V31.1658Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.35047 34.2247C4.35047 34.2907 4.29647 34.3447 4.23047 34.3447H0.786488C0.750488 34.3447 0.726489 34.3687 0.726489 34.4047V35.3226C0.726489 35.3886 0.672489 35.4426 0.606489 35.4426H0.270491C0.204491 35.4426 0.150491 35.3886 0.150491 35.3226L0.150491 32.7907C0.150491 32.7247 0.204491 32.6707 0.270491 32.6707H0.606489C0.672489 32.6707 0.726488 32.7247 0.726488 32.7907L0.726489 33.7087C0.726489 33.7447 0.750488 33.7687 0.786488 33.7687H4.23047C4.29647 33.7687 4.35047 33.8227 4.35047 33.8887V34.2247Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M4.41647 37.0915C4.41647 37.8835 3.95447 38.4655 3.13247 38.4655H3.09647C3.03047 38.4655 2.97647 38.4115 2.97647 38.3455V37.9915C2.97647 37.9255 3.03047 37.8715 3.09647 37.8715H3.14447C3.58847 37.8715 3.86447 37.5415 3.86447 37.0975C3.86447 36.6475 3.58247 36.3475 3.19847 36.3475C2.83247 36.3475 2.63448 36.5755 2.52048 36.9655L2.38248 37.4395C2.23248 37.9495 1.87848 38.3455 1.23648 38.3455C0.546486 38.3455 0.0844876 37.7815 0.0844876 37.0735C0.0844876 36.3535 0.534485 35.8135 1.21848 35.8135H1.28448C1.35048 35.8135 1.40448 35.8675 1.40448 35.9335V36.2755C1.40448 36.3415 1.35048 36.3955 1.28448 36.3955H1.22448C0.858484 36.3955 0.636485 36.6595 0.636485 37.0735C0.636485 37.4695 0.852484 37.7515 1.21848 37.7515C1.57848 37.7515 1.75248 37.4935 1.85448 37.1755L1.99248 36.7195C2.16648 36.1375 2.52048 35.7535 3.17447 35.7535C3.90047 35.7535 4.41647 36.3055 4.41647 37.0915Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M2.10048 1.76122C2.10048 1.79421 2.07348 1.82121 2.04048 1.82121L0.0604882 1.82121C0.0274882 1.82121 0.000488338 1.79422 0.000488337 1.76122L0.000488325 1.49722C0.000488324 1.46422 0.0154882 1.44322 0.0514881 1.43422L0.897484 1.18522C0.924484 1.17622 0.927484 1.14322 0.897484 1.13422L0.0514881 0.888219C0.0154882 0.876219 0.000488297 0.858219 0.000488296 0.825219L0.000488284 0.561221C0.000488283 0.528221 0.0274882 0.501221 0.0604881 0.501221L2.04048 0.501221C2.07348 0.501221 2.10048 0.528221 2.10048 0.561221V0.70522C2.10048 0.73822 2.07348 0.76522 2.04048 0.76522L0.561486 0.76522C0.531486 0.76522 0.525486 0.804219 0.555486 0.813219L1.32048 1.02922C1.37748 1.04422 1.39248 1.06222 1.39248 1.09522V1.22422C1.39248 1.25722 1.37748 1.27822 1.32048 1.29322L0.555486 1.50922C0.525486 1.51822 0.531486 1.55722 0.561486 1.55722L2.04048 1.55722C2.07348 1.55722 2.10048 1.58422 2.10048 1.61722V1.76122Z"
                    fill="#EBEBEE"
                  />
                  <path
                    d="M2.10048 2.77462C2.10048 2.80762 2.07348 2.83462 2.04048 2.83462H0.318487C0.300487 2.83462 0.288487 2.84662 0.288487 2.86462V3.32362C0.288487 3.35662 0.261487 3.38362 0.228487 3.38362H0.0604882C0.0274882 3.38362 0.000488338 3.35662 0.000488337 3.32362L0.000488281 2.05762C0.00048828 2.02462 0.0274882 1.99762 0.0604881 1.99762H0.228487C0.261487 1.99762 0.288487 2.02462 0.288487 2.05762L0.288487 2.51662C0.288487 2.53462 0.300487 2.54662 0.318487 2.54662H2.04048C2.07348 2.54662 2.10048 2.57362 2.10048 2.60662V2.77462Z"
                    fill="#EBEBEE"
                  />
                </svg>
              </div>
              <div
                className="absolute left-[5%] top-[50%] w-[90%] h-[22%] rounded-md"
                style={{
                  border: '1px solid var(--_Lil-Bottom-Highlight, rgba(255, 255, 255, 0.00))',
                  background: 'linear-gradient(0deg, #333536,#45474a)',
                  backgroundOrigin: 'border-box',
                  backgroundRepeat: 'no-repeat',
                  outline: '1px solid rgba(255, 255, 255, 0.15)',
                }}
              >
                <div
                  className="p-[2px] rounded-[4px] w-full h-full font-pressura-mono"
                  style={{
                    background: 'linear-gradient(0deg, #333536, #393a3c)',
                  }}
                >
                  <div
                    className={cn(
                      'p-2 flex flex-col justify-between leading-none w-full h-full bg-[#030303] rounded-sm text-center uppercase',
                      innerClassName,
                    )}
                  >
                    <div className="text-[#B7B7BD] uppercase" style={{ fontSize: `${fontSize}px` }}>
                      {title}
                    </div>

                    <div className="relative text-[#fff] overflow-hidden whitespace-nowrap tracking-[-0.32px] leading-[1]">
                      <span
                        className="w-[8px] h-full inline-block absolute left-0 top-0 uppercase"
                        style={{
                          fontSize: `${fontSize * 1.33}px`,
                          background: 'linear-gradient(90deg, #030303 0%, rgba(3, 3, 3, 0.00) 100%)',
                        }}
                      ></span>
                      <span style={{ fontSize: `${fontSize * 1.33}px` }}>{content}</span>
                      <span
                        className="w-[8px] h-full inline-block absolute top-0 right-0"
                        style={{
                          fontSize: `${fontSize * 1.33}px`,
                          background: 'linear-gradient(270deg, #030303 0%, rgba(3, 3, 3, 0.00) 100%)',
                        }}
                      ></span>
                    </div>
                    <div className="flex justify-between text-[#71717A]" style={{ fontSize: `${fontSize}px` }}>
                      <div className="flex justify-center items-center">
                        <span className="mr-1">PWR</span>
                        <span>{valueMapWord[value as 0 | 1] || value}</span>
                      </div>
                      <div>
                        {showRenewButton ? (
                          <>
                            <span
                              className="cursor-pointer text-jen-active-primary underline  inset-0 flex items-center justify-center"
                              onClick={() => {
                                open();
                              }}
                            >
                              <InfoIcon />
                              RENEW MINS
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="mr-1">{timeCount}</span>
                            <span className="">MINS LEFT</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {showAdvancedStrengthControl ? (
                <StyleFilterStrength
                  className="absolute left-[18px] bottom-[12px] w-[188px]"
                  onChange={(weight) => {
                    setValue(weight);
                    onSwitch?.({ value: weight, id: 0 });
                  }}
                  initValue={value}
                />
              ) : (
                <ToggleSwitch
                  defaultValue={defaultValue}
                  onChange={(value) => {
                    setValue(value === 'LOW' ? 0 : 1);
                    onSwitch?.({ value: value === 'LOW' ? 0 : 1, id: sfList?.[value === 'LOW' ? 0 : 1]?.id });
                  }}
                  className="absolute top-[75%] w-[90%] left-[5%] h-[20%]"
                  fontSize={fontSize}
                  disabled={switchDisabled}
                />
              )}
            </div>
          )}
          {sfId && selectedSfs && selectedSfs.length === 2 && <MultiSFSelectionUI selectedSfs={selectedSfs} />}
          {!sfId && <NoneSfSelectedUI />}

          {showButtons && (
            <div className="flex justify-center w-full h-[80px] p-[4px] items-center">
              <GradientButton
                disabled={removeButtonDisabled}
                type="button"
                className="text-[#111112] hover:bg-orange-600 border border-[#111112] radius-xs"
                onClick={() => {
                  onRemove?.();
                }}
                text={<span className="text-[#efeff0] text-[12px]">REMOVE</span>}
                icon={<CloseIcon fillColor="#efeff0" />}
                width="35%"
              />
              <GradientButton
                disabled={useButtonDisabled}
                type="button"
                className=" hover:bg-gradient-to-br hover:from-[#ff5b27] hover:to-[#c24017]  radius-xs ml-1"
                onClick={() => {
                  isEmpty(sfId) && onUse?.({ value: value, id: sfId });
                }}
                text={
                  <span className="text-[#efeff0] text-[12px]">USE FILTER{selectedSfs ? (selectedSfs.length > 1 ? 'S' : '') : ''}</span>
                }
                width="65%"
                icon={<UseFilterIcon fillColor="#efeff0" />}
              />
            </div>
          )}
        </div>
      </div>
      <RenewFilterModal />
    </div>
  );
};

const NoneSfSelectedUI = () => {
  return (
    <div className="gap-[13px] p-6 flex-1 flex flex-col items-center justify-center text-[#999CA0]">
      <div className="h-6" />
      <h2 className="text-[1.2rem] font-medium opacity-40 ">No Filter Selected</h2>
      <p className="text-[14px] opacity-40 text-center leading-tight tracking-[-0.42px]">
        Select a filter from the list to add to your track.
      </p>
    </div>
  );
};

const MultiSFSelectionUI = ({ selectedSfs }: { selectedSfs?: FilterGroup[] }) => {
  const mainSf = selectedSfs ? selectedSfs[0] : undefined;
  const secondarySf = selectedSfs ? selectedSfs[1] : undefined;

  return (
    <div className="text-[#999CA0]">
      <div className="text-center mb-4 mt-4">
        <div className="text-[14px] leading-4">Select one StyleFilter™ to add to a track or select two to create Soulmate.</div>
      </div>

      <div className="space-y-4 mb-4 flex flex-col items-center">
        <div
          className={`relative rounded-lg p-6 h-32 w-24 flex flex-col justify-center items-center cursor-default transition-all duration-200 ${mainSf === undefined ? 'border border-jen-orange-60' : ''}`}
        >
          {mainSf === undefined && (
            <div className="text-center">
              <div className="text-[12px] font-medium">MAIN</div>
              <div className="text-[12px] font-medium">STYLEFILTER™</div>
            </div>
          )}
          {mainSf !== undefined && (
            <div
              className={`absolute left-0 top-0 block w-full h-full text-center rounded object-cover bg-cover flex-shrink-0 ${(mainSf.thumb_album_cover ?? mainSf.album_cover) ? '' : 'bg-jen-orange'}`}
            >
              {(mainSf.thumb_album_cover ?? mainSf.album_cover) ? (
                <img
                  src={mainSf.thumb_album_cover ?? mainSf.album_cover}
                  className={cn('w-32 h-32 block rounded object-cover bg-cover flex-shrink-0 ')}
                />
              ) : (
                <div className="absolute bottom-[26px] w-full">
                  <span className="text-[22px] uppercase text-white">{getInitials(mainSf.artist)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={`relative rounded-lg p-6 h-32 w-24 flex flex-col justify-center items-center cursor-default transition-all duration-200 ${secondarySf === undefined ? 'border border-white/20' : ''}`}
        >
          {secondarySf === undefined && (
            <div className="text-center">
              <div className="text-[12px] font-medium">SOULMATE</div>
              <div className="text-[12px] font-medium">STYLEFILTER™</div>
            </div>
          )}
          {secondarySf !== undefined && (
            <div
              className={`absolute left-0 top-0 block w-full h-full text-center rounded object-cover bg-cover flex-shrink-0 ${(secondarySf.thumb_album_cover ?? secondarySf.album_cover) ? '' : 'bg-jen-orange'}`}
            >
              {(secondarySf.thumb_album_cover ?? secondarySf.album_cover) ? (
                <img
                  src={secondarySf.thumb_album_cover ?? secondarySf.album_cover}
                  className={cn('w-32 h-32 block rounded object-cover bg-cover flex-shrink-0 ')}
                />
              ) : (
                <div className="absolute bottom-[26px] w-full">
                  <span className="text-[22px] uppercase text-white">{getInitials(secondarySf.artist)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StyleFilterDetailCard;
